import React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import {
    ChevronLeft, ChevronRight, Maximize2, Minus, Plus, X,
} from 'lucide-react';
import { cn } from './utils';

/**
 * Full-screen image viewer with zoom and pan.
 *
 * Built on the Radix Dialog primitives rather than our styled <DialogContent>: that one
 * is a centred, padded, max-w-lg white card, and every one of those is wrong for a
 * photo viewer. Radix still gives us the focus trap, scroll lock and Esc handling.
 *
 * NO NEW DEPENDENCY. A lightbox library would have been ~40kB for what is a transform
 * on a single <img> — and the pointer handling below is the part those libraries get
 * wrong on a phone anyway, because the yard uses this one-handed with gloves on.
 *
 * Interactions:
 *   click thumbnail   open here
 *   wheel / pinch     zoom toward the cursor or the pinch midpoint, not the centre
 *   drag              pan, only once zoomed in
 *   double click/tap  toggle between fit and 2x
 *   arrows            previous / next photo
 *   + - 0             zoom in, out, reset
 *   Esc               close
 */

export interface LightboxImage {
    id: number | string;
    url: string;
    /** Shown bottom-left — the photo's stage, slot label, or whatever names it. */
    label?: string | null;
    /** Shown under the label, smaller. Who took it, when. */
    caption?: string | null;
}

interface Props {
    images: LightboxImage[];
    /** Index to show, or null when closed. */
    index: number | null;
    onIndexChange: (index: number | null) => void;
    /** Used as the alt text prefix, e.g. "Load photo". */
    alt?: string;
}

const MIN_SCALE = 1;
const MAX_SCALE = 6;
const STEP = 0.5;

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

export function ImageLightbox({ images, index, onIndexChange, alt = 'Photo' }: Props) {
    const open = index !== null && index >= 0 && index < images.length;
    const image = open ? images[index as number] : null;

    // One piece of state, not three. Zooming toward a point has to change scale and both
    // offsets together — as separate states that means calling a setter inside another
    // setter's updater, which React 18 double-invokes under StrictMode.
    const [view, setView] = React.useState({ scale: 1, tx: 0, ty: 0 });
    const { scale, tx, ty } = view;
    /** True while a finger or button is down, so the transform does not animate mid-drag. */
    const [interacting, setInteracting] = React.useState(false);

    const frameRef = React.useRef<HTMLDivElement | null>(null);
    /**
     * The frame as STATE as well as a ref. Radix mounts the portal content a beat
     * after the first commit, so an effect reading frameRef.current on the opening
     * render sees null, bails, and — its deps unchanged — never runs again: the wheel
     * listener only attached after something else changed scale (UAT D27: "the wheel
     * only works once the buttons have been used"). A callback ref re-fires when the
     * node actually appears, which re-runs the listener effect below.
     */
    const [frameEl, setFrameEl] = React.useState<HTMLDivElement | null>(null);
    const setFrame = React.useCallback((node: HTMLDivElement | null) => {
        frameRef.current = node;
        setFrameEl(node);
    }, []);
    const imgRef = React.useRef<HTMLImageElement | null>(null);
    /** Live pointers, so one finger pans and two pinch. */
    const pointers = React.useRef(new Map<number, { x: number; y: number }>());
    const pinchRef = React.useRef<{ distance: number; scale: number } | null>(null);
    const dragRef = React.useRef<{ x: number; y: number; tx: number; ty: number } | null>(null);

    const reset = React.useCallback(() => setView({ scale: 1, tx: 0, ty: 0 }), []);

    // A new photo always starts fitted — carrying zoom across images means landing on a
    // crop of something you have not seen whole yet.
    React.useEffect(() => { reset(); }, [index, reset]);

    /**
     * Keeps the image from being dragged off screen: at a given scale it may only move
     * by however much it overflows the frame.
     */
    const clampPan = React.useCallback((nx: number, ny: number, s: number) => {
        const frame = frameRef.current;
        const img = imgRef.current;
        if (!frame || !img) return { x: nx, y: ny };
        const maxX = Math.max(0, (img.clientWidth * s - frame.clientWidth) / 2);
        const maxY = Math.max(0, (img.clientHeight * s - frame.clientHeight) / 2);
        return { x: clamp(nx, -maxX, maxX), y: clamp(ny, -maxY, maxY) };
    }, []);

    /**
     * Zooms so the point under (clientX, clientY) stays under it. Zooming from the
     * centre is what makes most viewers feel like they are fighting you — you aim at a
     * damaged end plate and it slides away.
     */
    const zoomAt = React.useCallback((nextScale: number, clientX?: number, clientY?: number) => {
        setView((prev) => {
            const s = clamp(nextScale, MIN_SCALE, MAX_SCALE);
            if (s === MIN_SCALE) return { scale: MIN_SCALE, tx: 0, ty: 0 };

            const frame = frameRef.current;
            if (!frame || clientX === undefined || clientY === undefined) {
                const kept = clampPan(prev.tx, prev.ty, s);
                return { scale: s, tx: kept.x, ty: kept.y };
            }

            const rect = frame.getBoundingClientRect();
            const px = clientX - rect.left - rect.width / 2;
            const py = clientY - rect.top - rect.height / 2;
            const ratio = s / prev.scale;

            const next = clampPan(
                px - (px - prev.tx) * ratio,
                py - (py - prev.ty) * ratio,
                s,
            );
            return { scale: s, tx: next.x, ty: next.y };
        });
    }, [clampPan]);

    /* ------------------------------------------------------------- pointers */

    const distanceBetween = () => {
        const [a, b] = Array.from(pointers.current.values());
        return Math.hypot(a.x - b.x, a.y - b.y);
    };
    const midpointOf = () => {
        const [a, b] = Array.from(pointers.current.values());
        return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
    };

    const onPointerDown = (e: React.PointerEvent) => {
        pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
        (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
        setInteracting(true);

        if (pointers.current.size === 2) {
            dragRef.current = null;
            pinchRef.current = { distance: distanceBetween(), scale };
        } else if (pointers.current.size === 1 && scale > MIN_SCALE) {
            dragRef.current = { x: e.clientX, y: e.clientY, tx, ty };
        }
    };

    const onPointerMove = (e: React.PointerEvent) => {
        if (!pointers.current.has(e.pointerId)) return;
        pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

        if (pointers.current.size === 2 && pinchRef.current) {
            const start = pinchRef.current;
            const mid = midpointOf();
            zoomAt(start.scale * (distanceBetween() / start.distance), mid.x, mid.y);
            return;
        }
        if (dragRef.current) {
            const d = dragRef.current;
            setView((prev) => {
                const next = clampPan(
                    d.tx + (e.clientX - d.x), d.ty + (e.clientY - d.y), prev.scale);
                return { ...prev, tx: next.x, ty: next.y };
            });
        }
    };

    const endPointer = (e: React.PointerEvent) => {
        pointers.current.delete(e.pointerId);
        if (pointers.current.size < 2) pinchRef.current = null;
        if (pointers.current.size === 0) {
            dragRef.current = null;
            setInteracting(false);
        }
    };

    /**
     * Non-passive wheel listener. React's onWheel is passive, so preventDefault() there
     * is ignored and the page scrolls behind the viewer while you zoom.
     */
    React.useEffect(() => {
        if (!frameEl || !open) return undefined;
        const handler = (e: WheelEvent) => {
            e.preventDefault();
            zoomAt(scale * (e.deltaY < 0 ? 1.15 : 1 / 1.15), e.clientX, e.clientY);
        };
        frameEl.addEventListener('wheel', handler, { passive: false });
        return () => frameEl.removeEventListener('wheel', handler);
    }, [open, frameEl, scale, zoomAt]);

    /* ------------------------------------------------------------- keyboard */

    const go = React.useCallback((delta: number) => {
        if (index === null) return;
        const next = index + delta;
        if (next >= 0 && next < images.length) onIndexChange(next);
    }, [index, images.length, onIndexChange]);

    React.useEffect(() => {
        if (!open) return undefined;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'ArrowLeft') { e.preventDefault(); go(-1); }
            if (e.key === 'ArrowRight') { e.preventDefault(); go(1); }
            if (e.key === '+' || e.key === '=') { e.preventDefault(); zoomAt(scale + STEP); }
            if (e.key === '-' || e.key === '_') { e.preventDefault(); zoomAt(scale - STEP); }
            if (e.key === '0') { e.preventDefault(); reset(); }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, go, scale, zoomAt, reset]);

    if (!open || !image) return null;

    const zoomed = scale > MIN_SCALE;

    return (
        <DialogPrimitive.Root open onOpenChange={(v) => !v && onIndexChange(null)}>
            <DialogPrimitive.Portal>
                <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/90 data-[state=open]:animate-in data-[state=open]:fade-in-0" />
                <DialogPrimitive.Content
                    aria-describedby={undefined}
                    className="fixed inset-0 z-50 flex flex-col outline-none data-[state=open]:animate-in data-[state=open]:fade-in-0"
                >
                    <DialogPrimitive.Title className="sr-only">
                        {alt} {(index as number) + 1} of {images.length}
                    </DialogPrimitive.Title>

                    {/* Toolbar */}
                    <div className="flex items-center justify-between gap-2 p-3 text-white/90">
                        <span className="text-sm tabular-nums pl-1">
                            {(index as number) + 1} / {images.length}
                        </span>
                        <div className="flex items-center gap-1">
                            <ToolButton label="Zoom out" onClick={() => zoomAt(scale - STEP)}
                                        disabled={scale <= MIN_SCALE}>
                                <Minus size={18} />
                            </ToolButton>
                            <span className="text-xs tabular-nums w-12 text-center select-none">
                                {Math.round(scale * 100)}%
                            </span>
                            <ToolButton label="Zoom in" onClick={() => zoomAt(scale + STEP)}
                                        disabled={scale >= MAX_SCALE}>
                                <Plus size={18} />
                            </ToolButton>
                            <ToolButton label="Reset zoom" onClick={reset} disabled={!zoomed}>
                                <Maximize2 size={18} />
                            </ToolButton>
                            <DialogPrimitive.Close asChild>
                                <ToolButton label="Close"><X size={18} /></ToolButton>
                            </DialogPrimitive.Close>
                        </div>
                    </div>

                    {/* Stage */}
                    <div
                        ref={setFrame}
                        className={cn('relative flex-1 overflow-hidden touch-none select-none',
                            zoomed ? 'cursor-grab active:cursor-grabbing' : 'cursor-zoom-in')}
                        onPointerDown={onPointerDown}
                        onPointerMove={onPointerMove}
                        onPointerUp={endPointer}
                        onPointerCancel={endPointer}
                        onDoubleClick={(e) => (zoomed ? reset() : zoomAt(2, e.clientX, e.clientY))}
                    >
                        <div className="absolute inset-0 flex items-center justify-center">
                            <img
                                ref={imgRef}
                                src={image.url}
                                alt={`${alt} ${(index as number) + 1}`}
                                draggable={false}
                                onLoad={reset}
                                className="max-h-full max-w-full object-contain will-change-transform"
                                style={{
                                    transform: `translate3d(${tx}px, ${ty}px, 0) scale(${scale})`,
                                    transition: interacting ? 'none' : 'transform 120ms ease-out',
                                }}
                            />
                        </div>

                        {images.length > 1 && (
                            <>
                                <NavButton side="left" label="Previous photo"
                                           disabled={(index as number) === 0}
                                           onClick={() => go(-1)}>
                                    <ChevronLeft size={22} />
                                </NavButton>
                                <NavButton side="right" label="Next photo"
                                           disabled={(index as number) === images.length - 1}
                                           onClick={() => go(1)}>
                                    <ChevronRight size={22} />
                                </NavButton>
                            </>
                        )}
                    </div>

                    {(image.label || image.caption) && (
                        <div className="p-4 text-white/90">
                            {image.label && <p className="text-sm font-medium">{image.label}</p>}
                            {image.caption && <p className="text-xs text-white/60">{image.caption}</p>}
                        </div>
                    )}
                </DialogPrimitive.Content>
            </DialogPrimitive.Portal>
        </DialogPrimitive.Root>
    );
}

const ToolButton = React.forwardRef<HTMLButtonElement, {
    label: string; onClick?: () => void; disabled?: boolean; children: React.ReactNode;
}>(({ label, onClick, disabled, children }, ref) => (
    <button
        ref={ref}
        type="button"
        aria-label={label}
        title={label}
        onClick={onClick}
        disabled={disabled}
        className="h-9 w-9 inline-flex items-center justify-center rounded-md hover:bg-white/15 disabled:opacity-30 disabled:hover:bg-transparent focus:outline-none focus:ring-2 focus:ring-white/40"
    >
        {children}
    </button>
));
ToolButton.displayName = 'ToolButton';

function NavButton({ side, label, disabled, onClick, children }: {
    side: 'left' | 'right'; label: string; disabled: boolean;
    onClick: () => void; children: React.ReactNode;
}) {
    return (
        <button
            type="button"
            aria-label={label}
            onClick={onClick}
            disabled={disabled}
            // Stops a click on the arrow from also reaching the stage's double-click handler.
            onPointerDown={(e) => e.stopPropagation()}
            className={cn(
                'absolute top-1/2 -translate-y-1/2 h-11 w-11 inline-flex items-center justify-center',
                'rounded-full bg-black/50 text-white hover:bg-black/70',
                // Dimmed at the ends, NOT hidden. Hiding it made the first photo look as
                // though there was no way back, so you would close the viewer to go there.
                'disabled:opacity-25 disabled:hover:bg-black/50 disabled:cursor-default',
                'focus:outline-none focus:ring-2 focus:ring-white/40',
                side === 'left' ? 'left-3' : 'right-3',
            )}
        >
            {children}
        </button>
    );
}

export default ImageLightbox;
